import { api } from './_client';
import type {
  AnalyticsOverview as AnalyticsOverviewResponse,
  TrafficTimeseries as AnalyticsTrafficResponse,
  TrafficSources as AnalyticsSourcesResponse,
  TopPages as AnalyticsPagesResponse,
  GeographyData as AnalyticsGeographyResponse,
  DeviceBreakdown as AnalyticsDevicesResponse,
  UserTypes as AnalyticsUsersResponse,
  AnalyticsInsights,
  ActionLogEntry,
  ActionLogCreate,
  AnalyticsDigestEntry,
  Goal,
  GoalCreate,
  GoalUpdate,
  StrategyContext,
  StrategyUpdate,
  StrategyHistoryEntry,
  InsightFeedback,
  InsightFeedbackCreate,
} from '../../types/analytics';

export const analyticsApi = {
  async checkAnalyticsAccess(): Promise<{ has_access: boolean }> {
    const response = await api.get<{ has_access: boolean }>('/api/analytics/access');
    return response.data;
  },

  async getAnalyticsOverview(range: string): Promise<AnalyticsOverviewResponse> {
    const response = await api.get<AnalyticsOverviewResponse>('/api/analytics/overview', {
      params: { range },
    });
    return response.data;
  },

  async getAnalyticsTraffic(range: string): Promise<AnalyticsTrafficResponse> {
    const response = await api.get<AnalyticsTrafficResponse>('/api/analytics/traffic', {
      params: { range },
    });
    return response.data;
  },

  async getAnalyticsSources(range: string): Promise<AnalyticsSourcesResponse> {
    const response = await api.get<AnalyticsSourcesResponse>('/api/analytics/sources', {
      params: { range },
    });
    return response.data;
  },

  async getAnalyticsPages(range: string, limit = 10): Promise<AnalyticsPagesResponse> {
    const response = await api.get<AnalyticsPagesResponse>('/api/analytics/pages', {
      params: { range, limit },
    });
    return response.data;
  },

  async getAnalyticsGeography(range: string): Promise<AnalyticsGeographyResponse> {
    const response = await api.get<AnalyticsGeographyResponse>('/api/analytics/geography', {
      params: { range },
    });
    return response.data;
  },

  async getAnalyticsDevices(range: string): Promise<AnalyticsDevicesResponse> {
    const response = await api.get<AnalyticsDevicesResponse>('/api/analytics/devices', {
      params: { range },
    });
    return response.data;
  },

  async getAnalyticsUsers(range: string): Promise<AnalyticsUsersResponse> {
    const response = await api.get<AnalyticsUsersResponse>('/api/analytics/users', {
      params: { range },
    });
    return response.data;
  },

  // --- Action Log ---

  async getAnalyticsActionLog(): Promise<ActionLogEntry[]> {
    const response = await api.get<ActionLogEntry[]>('/api/analytics/action-log');
    return response.data;
  },

  async createAnalyticsActionLogEntry(entry: ActionLogCreate): Promise<ActionLogEntry> {
    const response = await api.post<ActionLogEntry>('/api/analytics/action-log', entry);
    return response.data;
  },

  async deleteAnalyticsActionLogEntry(id: string): Promise<void> {
    await api.delete(`/api/analytics/action-log/${id}`);
  },

  // --- Insights ---

  async getAnalyticsInsightsLatest(): Promise<AnalyticsInsights | null> {
    const response = await api.get<AnalyticsInsights | null>('/api/analytics/insights/latest');
    return response.data;
  },

  // --- Digests Archive ---

  async getAnalyticsDigests(): Promise<AnalyticsDigestEntry[]> {
    const response = await api.get<AnalyticsDigestEntry[]>('/api/analytics/digests');
    return response.data;
  },

  async getAnalyticsDigest(id: string): Promise<AnalyticsDigestEntry> {
    const response = await api.get<AnalyticsDigestEntry>(`/api/analytics/digests/${id}`);
    return response.data;
  },

  // --- Goals (strategy layer) ---

  async listAnalyticsGoals(includeAbandoned = false): Promise<Goal[]> {
    const response = await api.get<Goal[]>('/api/analytics/goals', {
      params: { include_abandoned: includeAbandoned },
    });
    return response.data;
  },

  async createAnalyticsGoal(payload: GoalCreate): Promise<Goal> {
    const response = await api.post<Goal>('/api/analytics/goals', payload);
    return response.data;
  },

  async updateAnalyticsGoal(goalId: string, payload: GoalUpdate): Promise<Goal> {
    const response = await api.put<Goal>(
      `/api/analytics/goals/${encodeURIComponent(goalId)}`,
      payload
    );
    return response.data;
  },

  async deleteAnalyticsGoal(goalId: string): Promise<void> {
    await api.delete(`/api/analytics/goals/${encodeURIComponent(goalId)}`);
  },

  // --- Strategy doc ---

  async getAnalyticsStrategy(): Promise<StrategyContext> {
    const response = await api.get<StrategyContext>('/api/analytics/strategy');
    return response.data;
  },

  async updateAnalyticsStrategy(payload: StrategyUpdate): Promise<StrategyContext> {
    const response = await api.put<StrategyContext>('/api/analytics/strategy', payload);
    return response.data;
  },

  async listAnalyticsStrategyHistory(): Promise<StrategyHistoryEntry[]> {
    const response = await api.get<StrategyHistoryEntry[]>('/api/analytics/strategy/history');
    return response.data;
  },

  // --- Insight feedback ---

  async upsertAnalyticsInsightFeedback(payload: InsightFeedbackCreate): Promise<InsightFeedback> {
    const response = await api.post<InsightFeedback>('/api/analytics/insights/feedback', payload);
    return response.data;
  },

  async listAnalyticsInsightFeedback(weekStart: string): Promise<InsightFeedback[]> {
    const response = await api.get<InsightFeedback[]>(
      `/api/analytics/insights/${encodeURIComponent(weekStart)}/feedback`
    );
    return response.data;
  },

  async deleteAnalyticsInsightFeedback(feedbackId: string): Promise<void> {
    await api.delete(`/api/analytics/insights/feedback/${encodeURIComponent(feedbackId)}`);
  },
};
